import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm, type FieldErrors, type FieldError } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Save, Search, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentUser } from '@/lib/auth-context';
import { canActAsSupplier as canUserActAsSupplier } from '@/lib/permissions';
import { useCreateSupplier, useSuppliersList } from '@/features/masters/suppliers/useSuppliers';

import {
  DISPATCH_LOCATION_REQUIRED_MESSAGE,
  DISPATCH_STOPS_MAX,
  dispatchRequestCreateSchema,
  dispatchRequestTypeValues,
  type DispatchRequestCreateInput,
  type DispatchRequestDto,
  type DispatchRequestUpdateInput,
} from '../types';
import {
  useDispatchLocationPresets,
  useCreateSupplierRequest,
  useSaveDispatchLocationPreset,
  useUpdateSupplierRequest,
} from '../useDispatch';

const NS = 'operations';
const ZIP_CLOUD_SEARCH_URL = 'https://zipcloud.ibsnet.co.jp/api/search';

interface ZipCloudAddress {
  zipcode?: string;
  address1?: string;
  address2?: string;
  address3?: string;
}

interface ZipCloudResponse {
  status: number;
  message: string | null;
  results: ZipCloudAddress[] | null;
}

const normalizePostalCode = (value: string) => value.replace(/\D/g, '').slice(0, 7);

const formatPostalCode = (value: string) => {
  const normalized = normalizePostalCode(value);
  if (normalized.length <= 3) return normalized;
  return `${normalized.slice(0, 3)}-${normalized.slice(3)}`;
};

/** Postal-lookup helper state, one entry per pickup row (index-aligned). */
interface StopPostalState {
  code: string;
  error: string | null;
}

const EMPTY_POSTAL: StopPostalState = { code: '', error: null };

const emptyStop = () => ({ location: '', notes: '' });

const firstErrorMessage = (errors: FieldErrors<DispatchRequestCreateInput>): string | null => {
  const stack: unknown[] = Object.values(errors);
  while (stack.length > 0) {
    const current = stack.shift();
    if (!current || typeof current !== 'object') continue;
    const message = (current as FieldError).message;
    if (typeof message === 'string' && message.trim()) return message;
    stack.push(...Object.values(current as Record<string, unknown>));
  }
  return null;
};

interface SupplierRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog runs in edit mode and PATCHes the draft. */
  row?: DispatchRequestDto | null;
  processorEdit?: boolean;
}

/**
 * Supplier-facing create / edit form for a dispatch request draft. The
 * supplier picks a type, date, one or more pickup locations (引取場所, up to
 * `DISPATCH_STOPS_MAX`, in visiting order), and optional notes. Every stop
 * belongs to this one supplier and carries an address + order only — weight
 * stays one weighing per truck and items are still recorded at receiving.
 * `draft` rows and supplier-owned `rejected` rows can be edited (gated by the caller).
 */
export const SupplierRequestDialog = ({ open, onOpenChange, row, processorEdit = false }: SupplierRequestDialogProps) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const create = useCreateSupplierRequest();
  const update = useUpdateSupplierRequest();
  const createSupplier = useCreateSupplier();
  const saveLocationPreset = useSaveDispatchLocationPreset();
  const suppliersQuery = useSuppliersList({ active_only: true, per_page: 500 });
  const isEdit = !!row;
  const canActAsSupplier = canUserActAsSupplier(user);
  const suppliers = suppliersQuery.data?.rows ?? [];
  const [quickSupplierOpen, setQuickSupplierOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [postals, setPostals] = useState<StopPostalState[]>([EMPTY_POSTAL]);
  const [postalLookupIndex, setPostalLookupIndex] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [notifySupplier, setNotifySupplier] = useState(true);
  const [notifyPlatform, setNotifyPlatform] = useState(true);
  const [notifyProcessor, setNotifyProcessor] = useState(true);
  const postalLookupPending = postalLookupIndex !== null;

  const form = useForm<DispatchRequestCreateInput>({
    resolver: zodResolver(dispatchRequestCreateSchema),
    defaultValues: {
      supplier_company_id: '',
      request_type: 'dispatch_out',
      requested_date: new Date().toISOString().slice(0, 10),
      requested_time: '09:00',
      location: '',
      notes: '',
      vehicle_number: '',
      receivable_date: '',
      cargo_description: '',
      cargo_quantity: '',
      contact_name: '',
      contact_phone: '',
      stops: [emptyStop()],
    },
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'stops' });
  const selectedSupplierCompanyId = form.watch('supplier_company_id');
  const locationSupplierCompanyId = canActAsSupplier
    ? selectedSupplierCompanyId || undefined
    : undefined;
  const locationPresets = useDispatchLocationPresets({
    supplier_company_id: locationSupplierCompanyId,
    per_page: 50,
    enabled: !canActAsSupplier || !!locationSupplierCompanyId,
  });
  const locationDatalistId = `dispatch-location-presets-${row?.dispatch_request_id ?? 'new'}`;
  const requestType = form.watch('request_type');
  const rejectionSlots = row?.return_suggested_availability ?? [];
  const hasReturnFeedback = !!row?.rejection_reason || rejectionSlots.length > 0;

  useEffect(() => {
    if (!open) return;
    setQuickSupplierOpen(false);
    setNewSupplierName('');
    setPostalLookupIndex(null);
    setSubmitError(null);
    setFormError(null);
    setChangeReason('');
    setCcEmails('');
    setNotifySupplier(true);
    setNotifyPlatform(true);
    setNotifyProcessor(true);
    if (row) {
      // Legacy drafts (and any read without a stop list) still have only the
      // denormalized header address, so fall back to a single row.
      const storedStops = row.stops ?? [];
      const stops = storedStops.length
        ? storedStops.map((stop) => ({ location: stop.location, notes: stop.notes ?? '' }))
        : [{ location: row.location ?? '', notes: '' }];
      form.reset({
        supplier_company_id: row.supplier_company_id,
        request_type: row.request_type,
        requested_date: row.requested_date ?? new Date().toISOString().slice(0, 10),
        requested_time: row.requested_time?.slice(0, 5) ?? '09:00',
        location: stops[0].location,
        notes: row.notes ?? '',
        vehicle_number: row.vehicle_number ?? '',
        receivable_date: row.receivable_date ?? '',
        cargo_description: row.cargo_description ?? '',
        cargo_quantity: row.cargo_quantity ?? '',
        contact_name: row.contact_name ?? '',
        contact_phone: row.contact_phone ?? '',
        stops,
      });
      setPostals(stops.map(() => EMPTY_POSTAL));
    } else {
      form.reset({
        supplier_company_id: '',
        request_type: 'dispatch_out',
        requested_date: new Date().toISOString().slice(0, 10),
        requested_time: '09:00',
        location: '',
        notes: '',
        vehicle_number: '',
        receivable_date: '',
        cargo_description: '',
        cargo_quantity: '',
        contact_name: '',
        contact_phone: '',
        stops: [emptyStop()],
      });
      setPostals([EMPTY_POSTAL]);
    }
  }, [open, row, form]);

  // The BE derives the header `location` from stop 1. Mirror it here so the
  // schema's still-required `location` and the payload can never disagree.
  const firstStopLocation = form.watch('stops.0.location') ?? '';
  useEffect(() => {
    form.setValue('location', firstStopLocation);
  }, [firstStopLocation, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    if (isEdit && !changeReason.trim()) {
      setFormError(t('efs.operations.dismantling.errors.reason_required'));
      return;
    }
    if (canActAsSupplier && !values.supplier_company_id) {
      form.setError('supplier_company_id', {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.supplier_required', {
          defaultValue: '請選擇廠商',
        }),
      });
      return;
    }

    const stops = values.stops.map((stop) => ({
      location: stop.location,
      notes: stop.notes?.trim() ? stop.notes : undefined,
    }));

    setSubmitError(null);
    try {
      if (isEdit && row) {
        const body: DispatchRequestUpdateInput = {
          supplier_company_id: canActAsSupplier ? values.supplier_company_id : undefined,
          request_type: values.request_type,
          requested_date: values.requested_date,
          requested_time: values.requested_time,
          location: stops[0].location,
          notes: values.notes ?? '',
          vehicle_number: values.vehicle_number ?? '',
          receivable_date: values.receivable_date || undefined,
          cargo_description: values.cargo_description ?? '',
          cargo_quantity: values.cargo_quantity ?? '',
          contact_name: values.contact_name ?? '',
          contact_phone: values.contact_phone ?? '',
          stops,
          change_reason: changeReason.trim() || undefined,
          notify_supplier: notifySupplier,
          notify_platform: notifyPlatform,
          notify_processor: notifyProcessor,
          cc_emails: ccEmails.split(',').map((email) => email.trim()).filter(Boolean),
        };
        const processorBody: DispatchRequestUpdateInput = {
          ...(row.request_type === 'dispatch_out'
            ? { vehicle_number: values.vehicle_number ?? '' }
            : {}),
          receivable_date: values.receivable_date || undefined,
          change_reason: changeReason.trim(),
          notify_supplier: notifySupplier,
          notify_platform: notifyPlatform,
          notify_processor: notifyProcessor,
          cc_emails: body.cc_emails,
        };
        await update.mutateAsync({
          id: row.dispatch_request_id,
          body: processorEdit ? processorBody : body,
          processor: processorEdit,
        });
      } else {
        await create.mutateAsync({
          ...values,
          location: stops[0].location,
          stops,
          vehicle_number: values.vehicle_number || undefined,
          receivable_date: values.receivable_date || undefined,
          cargo_description: values.cargo_description || undefined,
          cargo_quantity: values.cargo_quantity || undefined,
          contact_name: values.contact_name || undefined,
          contact_phone: values.contact_phone || undefined,
          supplier_company_id: canActAsSupplier ? values.supplier_company_id : undefined,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err);
    }
  }, (errors) => {
    setSubmitError(null);
    setFormError(
      firstErrorMessage(errors) ??
        t('efs.operations.dispatch.validation.form_invalid', {
          defaultValue: '入力内容を確認してください。',
        }),
    );
  });

  const onQuickCreateSupplier = async () => {
    const name = newSupplierName.trim();
    if (!name) {
      form.setError('supplier_company_id', {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.supplier_name_required', {
          defaultValue: '請輸入供應商名稱',
        }),
      });
      return;
    }
    try {
      const supplier = await createSupplier.mutateAsync({
        legal_name: name,
        display_name: name,
      });
      form.setValue('supplier_company_id', supplier.company_id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setNewSupplierName('');
      setQuickSupplierOpen(false);
    } catch (err) {
      form.setError('supplier_company_id', {
        type: 'manual',
        message: err instanceof Error ? err.message : '供應商新增失敗',
      });
    }
  };

  const setPostalAt = (index: number, patch: Partial<StopPostalState>) =>
    setPostals((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  const onAddStop = () => {
    append(emptyStop());
    setPostals((prev) => [...prev, EMPTY_POSTAL]);
  };

  const onRemoveStop = (index: number) => {
    remove(index);
    setPostals((prev) => prev.filter((_, i) => i !== index));
  };

  const onSaveLocationPreset = async (index: number) => {
    const locationPath = `stops.${index}.location` as const;
    const location = (form.getValues(locationPath) ?? '').trim();
    if (!location) {
      form.setError(locationPath, {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.location_required', {
          defaultValue: DISPATCH_LOCATION_REQUIRED_MESSAGE,
        }),
      });
      return;
    }
    if (canActAsSupplier && !selectedSupplierCompanyId) {
      form.setError('supplier_company_id', {
        type: 'manual',
        message: t('efs.operations.dispatch.validation.supplier_required', {
          defaultValue: '請選擇廠商',
        }),
      });
      return;
    }
    const preset = await saveLocationPreset.mutateAsync({
      supplier_company_id: canActAsSupplier ? selectedSupplierCompanyId : undefined,
      location,
    });
    form.setValue(locationPath, preset.location, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onPostalLookup = async (index: number) => {
    const locationPath = `stops.${index}.location` as const;
    const zipcode = normalizePostalCode(postals[index]?.code ?? '');
    if (zipcode.length !== 7) {
      setPostalAt(index, {
        error: t('efs.operations.dispatch.validation.postal_code_required', {
          defaultValue: '請輸入 7 碼郵遞區號',
        }),
      });
      return;
    }

    setPostalLookupIndex(index);
    setPostalAt(index, { error: null });
    try {
      const params = new URLSearchParams({ zipcode });
      const response = await fetch(`${ZIP_CLOUD_SEARCH_URL}?${params.toString()}`);
      if (!response.ok) throw new Error(`Postal lookup failed: ${response.status}`);

      const data = (await response.json()) as ZipCloudResponse;
      const result = data.results?.[0];
      const address = [result?.address1, result?.address2, result?.address3]
        .filter(Boolean)
        .join('');

      if (data.status !== 200 || !result || !address) {
        setPostalAt(index, {
          error:
            data.message ||
            t('efs.operations.dispatch.validation.postal_code_not_found', {
              defaultValue: '找不到此郵遞區號',
            }),
        });
        return;
      }

      setPostalAt(index, { code: formatPostalCode(result.zipcode ?? zipcode) });
      form.setValue(locationPath, address, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors(locationPath);
    } catch {
      setPostalAt(index, {
        error: t('efs.operations.dispatch.validation.postal_lookup_failed', {
          defaultValue: '地址查詢失敗，請稍後再試',
        }),
      });
    } finally {
      setPostalLookupIndex(null);
    }
  };

  const isPending =
    create.isPending ||
    update.isPending ||
    createSupplier.isPending ||
    saveLocationPreset.isPending ||
    postalLookupPending;

  const applySuggestedSlot = (slot: { date: string; start_time: string }) => {
    form.setValue('requested_date', slot.date, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('requested_time', slot.start_time.slice(0, 5), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-3xl lg:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('efs.operations.dispatch.dialog.edit_title', {
                  defaultValue: '配車・配送依頼を編集',
                })
              : t('efs.operations.dispatch.dialog.create_title', {
                  defaultValue: '配車・配送依頼を作成',
                })}
          </DialogTitle>
        </DialogHeader>

        {hasReturnFeedback ? (
          <Alert variant="destructive">
            <AlertTitle>
              {t('efs.operations.dispatch.dialog.rejected_notice_title', {
                defaultValue: '差し戻し内容を確認して修正してください',
              })}
            </AlertTitle>
            <AlertDescription className="space-y-3">
              {row?.rejection_reason ? (
                <p className="whitespace-pre-wrap">{row.rejection_reason}</p>
              ) : null}
              {rejectionSlots.length > 0 ? (
                <div className="space-y-2">
                  <p className="font-medium">
                    {t('efs.operations.dispatch.dialog.suggested_slots_label', {
                      defaultValue: '再配車可能日時',
                    })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {rejectionSlots.map((slot, index) => (
                      <Button
                        key={`${slot.date}-${slot.start_time}-${index}`}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => applySuggestedSlot(slot)}
                      >
                        {slot.date} {slot.start_time.slice(0, 5)}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              <p className="text-xs">
                {t('efs.operations.dispatch.dialog.rejected_notice_help', {
                  defaultValue:
                    '保存すると下書きに戻ります。内容を確認してから再送信してください。',
                })}
              </p>
            </AlertDescription>
          </Alert>
        ) : null}

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <fieldset disabled={processorEdit} className="space-y-4">
            <div className={canActAsSupplier ? 'grid gap-4 md:grid-cols-2' : 'grid gap-4'}>
              {canActAsSupplier ? (
                <FormField
                  control={form.control}
                  name="supplier_company_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.dispatch.fields.supplier')}</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Select
                            value={field.value ?? ''}
                            onValueChange={field.onChange}
                            disabled={suppliersQuery.isLoading}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t(
                                  'efs.operations.dispatch.fields.supplier_placeholder',
                                  { defaultValue: '請選擇供應商' },
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {suppliers.map((supplier) => (
                                <SelectItem key={supplier.company_id} value={supplier.company_id}>
                                  {supplier.display_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setQuickSupplierOpen((v) => !v)}
                          disabled={isPending}
                          title={t('efs.operations.dispatch.actions.quick_add_supplier', {
                            defaultValue: '快速新增供應商',
                          })}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {quickSupplierOpen ? (
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
                          <Input
                            value={newSupplierName}
                            onChange={(event) => setNewSupplierName(event.target.value)}
                            placeholder={t('efs.operations.dispatch.fields.supplier_name', {
                              defaultValue: '供應商名稱',
                            })}
                          />
                          <Button
                            type="button"
                            size="icon"
                            onClick={onQuickCreateSupplier}
                            disabled={isPending}
                            title={t('efs.common.actions.save', { ns: 'common' })}
                          >
                            {createSupplier.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setQuickSupplierOpen(false)}
                            disabled={isPending}
                            title={t('efs.common.actions.cancel', { ns: 'common' })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="request_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.type')}</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dispatchRequestTypeValues.map((rt) => (
                            <SelectItem key={rt} value={rt}>
                              {t(`efs.operations.dispatch.types.${rt}`, { defaultValue: rt })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="requested_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.requested_date')}</FormLabel>
                    <FormControl>
                      <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="requested_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.operations.dispatch.fields.requested_time', {
                        defaultValue: '希望時間',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            </fieldset>
            <div className="grid gap-4 md:grid-cols-2">
              {requestType === 'dispatch_out' ? (
                <FormField
                  control={form.control}
                  name="vehicle_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.dispatch.fields.vehicle_number')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          placeholder={t(
                            'efs.operations.dispatch.fields.vehicle_number_placeholder',
                            { defaultValue: '例：品川 500 あ 1234' },
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="receivable_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.operations.dispatch.fields.receivable_date')}</FormLabel>
                      <FormControl>
                        <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <fieldset disabled={processorEdit} className="space-y-4">
            <div className="space-y-3">
              {fields.map((stopField, index) => (
                <div key={stopField.id} className="space-y-2 rounded-md border p-3">
                  <FormField
                    control={form.control}
                    name={`stops.${index}.location`}
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between gap-2">
                          <FormLabel>
                            {t('efs.operations.dispatch.fields.stop_seq', {
                              defaultValue: '引取場所 {{seq}}',
                              seq: index + 1,
                            })}
                          </FormLabel>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemoveStop(index)}
                            disabled={isPending || fields.length === 1}
                            title={t('efs.operations.dispatch.actions.remove_stop', {
                              defaultValue: '引取場所を削除',
                            })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid gap-2 lg:grid-cols-[9rem_auto_minmax(0,1fr)_auto]">
                          <Input
                            value={postals[index]?.code ?? ''}
                            onChange={(event) =>
                              setPostalAt(index, {
                                code: formatPostalCode(event.target.value),
                                error: null,
                              })
                            }
                            placeholder={t('efs.operations.dispatch.fields.postal_code', {
                              defaultValue: '郵遞區號',
                            })}
                            inputMode="numeric"
                            autoComplete="postal-code"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="px-3"
                            onClick={() => onPostalLookup(index)}
                            disabled={isPending}
                            title={t('efs.operations.dispatch.actions.lookup_postal_code', {
                              defaultValue: '地址自動帶入',
                            })}
                          >
                            {postalLookupIndex === index ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4" />
                            )}
                            <span>{t('efs.operations.dispatch.actions.lookup_postal_code')}</span>
                          </Button>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value ?? ''}
                              list={locationDatalistId}
                              autoComplete="street-address"
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => onSaveLocationPreset(index)}
                            disabled={isPending}
                            title={t('efs.operations.dispatch.actions.save_location', {
                              defaultValue: '新增常用地點',
                            })}
                          >
                            {saveLocationPreset.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        {postals[index]?.error ? (
                          <p className="text-sm font-medium text-destructive">
                            {postals[index]?.error}
                          </p>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`stops.${index}.notes`}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            placeholder={t('efs.operations.dispatch.fields.stop_notes', {
                              defaultValue: 'この場所のメモ（任意）',
                            })}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}

              <datalist id={locationDatalistId}>
                {(locationPresets.data ?? []).map((preset) => (
                  <option key={preset.dispatch_location_preset_id} value={preset.location} />
                ))}
              </datalist>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAddStop}
                disabled={isPending || fields.length >= DISPATCH_STOPS_MAX}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('efs.operations.dispatch.actions.add_stop', {
                  defaultValue: '引取場所を追加',
                })}
              </Button>
            </div>

            {/* Cargo and the on-site contact window. Both travel into the
                submit / route notification mails, so the driver knows what to
                collect and who to ask for on arrival. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="cargo_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.cargo_description')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('efs.operations.dispatch.placeholders.cargo_description')}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cargo_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.cargo_quantity')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('efs.operations.dispatch.placeholders.cargo_quantity')}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.contact_name')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('efs.operations.dispatch.placeholders.contact_name')}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.operations.dispatch.fields.contact_phone')}</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="tel"
                        placeholder={t('efs.operations.dispatch.placeholders.contact_phone')}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.operations.dispatch.fields.notes')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            </fieldset>
            {isEdit ? (
              <div className="space-y-3 rounded-md border p-3">
                <div>
                  <label className="text-sm font-medium" htmlFor="dispatch-change-reason">
                    修改原因
                  </label>
                  <Input
                    id="dispatch-change-reason"
                    value={changeReason}
                    onChange={(event) => setChangeReason(event.target.value)}
                    placeholder="請簡述本次修改原因"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="dispatch-cc-emails">
                    CC Email
                  </label>
                  <Input
                    id="dispatch-cc-emails"
                    value={ccEmails}
                    onChange={(event) => setCcEmails(event.target.value)}
                    placeholder="多筆請以逗號分隔"
                  />
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label><input type="checkbox" checked={notifySupplier} onChange={(event) => setNotifySupplier(event.target.checked)} /> 供應商</label>
                  <label><input type="checkbox" checked={notifyPlatform} onChange={(event) => setNotifyPlatform(event.target.checked)} /> 平台商</label>
                  <label><input type="checkbox" checked={notifyProcessor} onChange={(event) => setNotifyProcessor(event.target.checked)} /> 處理商</label>
                </div>
              </div>
            ) : null}

            <LocalizedApiError
              error={submitError ?? update.error ?? create.error}
              showTechnicalDetails={false}
            />
            {formError ? (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {isEdit ? '確認修改／保存' : t('efs.common.actions.save', { ns: 'common' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierRequestDialog;
